import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useAnalytics,
  useAnalyticsLessons,
  type AnalyticsLessonFilters,
  type AnalyticsRange,
} from "@/api/queries";
import { Button, Card, SegmentedControl, StatTile } from "@/design-system";
import { Icon } from "@/components/Icon";
import { OfflineErrorState } from "@/components/OfflineErrorState";
import { RefreshButton } from "@/components/RefreshButton";
import { useAuth } from "@/auth";
import { useOnline } from "@/lib/useOnline";
import { formatNumber, formatShortDate } from "@/lib/format";
import { addLocalDays, getStudioTimezone, zonedDateKey } from "@/lib/timezone";
import type {
  AnalyticsInstructorBucket,
  AnalyticsLessonRow,
  AnalyticsOverview,
  AnalyticsStudent,
} from "@/lib/types";

/**
 * Studio analytics — the manager dashboard for `/teacher/analytics`,
 * guarded by `<RequireRole role="teacher">` in `App.tsx` (issue #292).
 *
 * A teacher-only aggregate view of how the studio is filling: fill rate
 * and utilisation over a date range, bucketed by day, hour and
 * instructor; the most active students; and a per-lesson break-down with
 * drill-down. The date range is filtered in the **studio timezone**, so
 * "today" and "18:00" match what the teacher sees elsewhere.
 *
 * Data comes from `GET /api/analytics` (`useAnalytics`); tapping an hour
 * bar or an instructor row narrows the lesson list via
 * `GET /api/analytics/lessons` (`useAnalyticsLessons`).
 */

const PRESETS = [7, 30, 90] as const;

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/** Studio-local "today" as `YYYY-MM-DD`. */
function today(): string {
  return zonedDateKey(new Date(), getStudioTimezone());
}

/** `YYYY-MM-DD` shifted by `days`, in plain calendar arithmetic. */
function shiftIso(dateIso: string, days: number): string {
  const [year, month, day] = dateIso.split("-").map(Number);
  const shifted = addLocalDays({ year, month, day }, days);
  return `${shifted.year}-${pad(shifted.month)}-${pad(shifted.day)}`;
}

function rangeForDays(days: number): AnalyticsRange {
  const to = today();
  return { from: shiftIso(to, -(days - 1)), to };
}

/** Drop unset filter keys so query keys stay stable and requests clean. */
function activeFilters(filters: AnalyticsLessonFilters): AnalyticsLessonFilters {
  const out: AnalyticsLessonFilters = {};
  if (filters.instructorId != null) out.instructorId = filters.instructorId;
  if (filters.hour != null) out.hour = filters.hour;
  return out;
}

/** "72%" from a `0..1` rate. */
function percent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function personName(name: string | null, email: string): string {
  return name?.trim() || email;
}

interface BarItem {
  key: string;
  label: string;
  value: number;
  hint: string;
}

/**
 * A tiny bar chart. Bars are real buttons when `onSelect` is given, so a
 * bucket is keyboard-reachable and can drive the drill-down; otherwise
 * they are a static, disabled visual.
 */
function BarChart({
  items,
  onSelect,
  selectedKey,
  testId,
  ariaLabel,
}: {
  items: BarItem[];
  onSelect?: (key: string) => void;
  selectedKey?: string | null;
  testId: string;
  ariaLabel: string;
}) {
  const max = Math.max(1, ...items.map((item) => item.value));

  return (
    <div role="group" aria-label={ariaLabel} data-testid={testId} style={{ marginTop: "var(--space-2)" }}>
      {/* Plot area. Every bar is absolutely positioned to the same
          baseline (`bottom: 0`) so the bars stay even regardless of
          which ones have a visible axis label; the labels live in a
          separate row below. */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: 3,
          height: 140,
        }}
      >
        {items.map((item) => {
          const heightPct = Math.round((item.value / max) * 100);
          const selected = selectedKey != null && selectedKey === item.key;

          return (
            <div
              key={item.key}
              style={{
                position: "relative",
                flex: 1,
                minWidth: 0,
                height: "100%",
              }}
            >
              <button
                type="button"
                onClick={onSelect ? () => onSelect(item.key) : undefined}
                disabled={!onSelect}
                title={item.hint}
                aria-label={item.hint}
                aria-pressed={onSelect ? selected : undefined}
                data-testid={`bar-${item.key}`}
                style={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  height: `${heightPct}%`,
                  minHeight: item.value > 0 ? 3 : 0,
                  padding: 0,
                  border: "none",
                  borderRadius: "var(--radius-sm) var(--radius-sm) 0 0",
                  cursor: onSelect ? "pointer" : "default",
                  background: selected ? "var(--brand-press)" : "#C06B47",
                  opacity: item.value > 0 ? 1 : 0.3,
                  transition:
                    "background var(--dur-base) var(--ease-out), height var(--dur-base) var(--ease-out)",
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Axis labels — one slot per bar so they line up, but only the
          sparse ones actually print text. */}
      <div style={{ display: "flex", gap: 3, marginTop: 4 }}>
        {items.map((item) => (
          <span
            key={item.key}
            style={{
              flex: 1,
              minWidth: 0,
              height: 12,
              textAlign: "center",
              font: "var(--fw-medium) var(--fs-2xs)/1 var(--font-mono)",
              color: "var(--text-faint)",
              whiteSpace: "nowrap",
            }}
          >
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

const ROW_LABEL_STYLE: CSSProperties = {
  font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
  letterSpacing: "var(--ls-wide)",
  textTransform: "uppercase",
  color: "var(--text-faint)",
};

const CELL_STYLE: CSSProperties = {
  font: "var(--fw-regular) var(--fs-sm)/1.4 var(--font-body)",
  color: "var(--text-strong)",
  padding: "10px 8px",
  textAlign: "left",
  borderTop: "1px solid var(--border-subtle)",
};

export function AnalyticsScreen() {
  const { t } = useTranslation();
  const online = useOnline();
  const { token } = useAuth();

  const [range, setRange] = useState<AnalyticsRange>(() => rangeForDays(30));
  const [preset, setPreset] = useState<string>("30");
  const [filters, setFilters] = useState<AnalyticsLessonFilters>({});

  const overview = useAnalytics(token, range);
  const cleanFilters = activeFilters(filters);
  const drilldown = useAnalyticsLessons(token, range, cleanFilters);
  const lessons: AnalyticsLessonRow[] = drilldown.data ?? [];

  const dayItems = useMemo<BarItem[]>(() => {
    const days = overview.data?.by_day ?? [];
    const step = Math.max(1, Math.ceil(days.length / 10));

    return days.map((bucket, index) => ({
      key: bucket.date,
      label: index % step === 0 ? formatShortDate(bucket.date) : "",
      value: bucket.fill_rate,
      hint: t("analytics.dayHint", {
        date: formatShortDate(bucket.date),
        booked: bucket.booked,
        capacity: bucket.capacity,
        rate: percent(bucket.fill_rate),
      }),
    }));
  }, [overview.data, t]);

  const hourItems = useMemo<BarItem[]>(
    () =>
      (overview.data?.by_hour ?? []).map((bucket) => ({
        key: String(bucket.hour),
        label: bucket.hour % 3 === 0 ? pad(bucket.hour) : "",
        value: bucket.booked,
        hint: t("analytics.hourHint", {
          hour: pad(bucket.hour),
          booked: bucket.booked,
          capacity: bucket.capacity,
        }),
      })),
    [overview.data, t],
  );

  function applyPreset(value: string) {
    setPreset(value);
    setFilters({});
    const days = Number(value);
    setRange(rangeForDays(days));
  }

  function applyBound(which: "from" | "to", value: string) {
    setPreset("custom");
    setRange((current) => ({ ...current, [which]: value }));
  }

  function toggleHour(key: string) {
    const hour = Number(key);
    setFilters((current) => ({
      ...current,
      hour: current.hour === hour ? undefined : hour,
    }));
  }

  function toggleInstructor(id: number) {
    setFilters((current) => ({
      ...current,
      instructorId: current.instructorId === id ? undefined : id,
    }));
  }

  const instructorName = useMemo(() => {
    const bucket = overview.data?.by_instructor.find(
      (entry) => entry.instructor_id === filters.instructorId,
    );
    return bucket?.name ?? null;
  }, [overview.data, filters.instructorId]);

  return (
    <div
      data-testid="analytics-screen"
      style={{
        padding: "var(--space-6) var(--space-4)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-5)",
        maxWidth: "var(--container-wide, 1120px)",
        marginInline: "auto",
        width: "100%",
      }}
    >
      <header
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--space-3)",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div>
          <div style={{ ...ROW_LABEL_STYLE, textTransform: "none" }}>
            {t("analytics.eyebrow")}
          </div>
          <h1
            style={{
              margin: "4px 0 0",
              font: "var(--fw-extra) var(--fs-2xl)/1.1 var(--font-display)",
              color: "var(--text-strong)",
              letterSpacing: "var(--ls-tight)",
            }}
          >
            {t("analytics.title")}
          </h1>
          <p
            style={{
              margin: "6px 0 0",
              font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
              color: "var(--text-muted)",
            }}
          >
            {t("analytics.subtitle")}
          </p>
        </div>
        <RefreshButton
          onClick={() => {
            void overview.refetch();
            void drilldown.refetch();
          }}
          refreshing={overview.isFetching || drilldown.isFetching}
          testId="analytics-refresh"
        />
      </header>

      <Card padding="var(--space-4)">
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--space-3)",
            alignItems: "flex-end",
            justifyContent: "space-between",
          }}
        >
          <SegmentedControl
            options={PRESETS.map((days) => ({
              value: String(days),
              label: t("analytics.rangeDays", { count: days }),
            }))}
            value={preset}
            onChange={applyPreset}
            size="sm"
          />

          <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap" }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={ROW_LABEL_STYLE}>{t("analytics.from")}</span>
              <input
                type="date"
                aria-label={t("analytics.from")}
                value={range.from}
                max={range.to}
                onChange={(event) => applyBound("from", event.target.value)}
                style={dateInputStyle}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={ROW_LABEL_STYLE}>{t("analytics.to")}</span>
              <input
                type="date"
                aria-label={t("analytics.to")}
                value={range.to}
                min={range.from}
                onChange={(event) => applyBound("to", event.target.value)}
                style={dateInputStyle}
              />
            </label>
          </div>
        </div>
      </Card>

      {overview.isPending ? (
        <Card>
          <div style={{ color: "var(--text-muted)", font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)" }}>
            {t("common.loading")}
          </div>
        </Card>
      ) : overview.isError ? (
        <Card>{online ? <ErrorState onRetry={() => void overview.refetch()} /> : <OfflineErrorState />}</Card>
      ) : overview.data ? (
        <AnalyticsBody
          overview={overview.data}
          lessons={lessons}
          loadingLessons={drilldown.isPending}
          filters={cleanFilters}
          instructorName={instructorName}
          dayItems={dayItems}
          hourItems={hourItems}
          onToggleHour={toggleHour}
          onToggleInstructor={toggleInstructor}
          onClearFilters={() => setFilters({})}
        />
      ) : null}
    </div>
  );
}

const dateInputStyle: CSSProperties = {
  font: "var(--fw-medium) var(--fs-sm)/1 var(--font-mono)",
  color: "var(--text-strong)",
  background: "var(--surface-card)",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  padding: "8px 10px",
};

function ErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: "center", padding: "var(--space-6) var(--space-4)" }}>
      <div
        style={{
          font: "var(--fw-bold) var(--fs-lg)/1.2 var(--font-display)",
          color: "var(--text-strong)",
          marginBottom: 8,
        }}
      >
        {t("analytics.loadErrorTitle")}
      </div>
      <div
        style={{
          font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
          color: "var(--text-muted)",
          marginBottom: "var(--space-4)",
        }}
      >
        {t("analytics.loadErrorBody")}
      </div>
      <Button type="button" variant="secondary" onClick={onRetry}>
        {t("common.retry")}
      </Button>
    </div>
  );
}

function AnalyticsBody({
  overview,
  lessons,
  loadingLessons,
  filters,
  instructorName,
  dayItems,
  hourItems,
  onToggleHour,
  onToggleInstructor,
  onClearFilters,
}: {
  overview: AnalyticsOverview;
  lessons: AnalyticsLessonRow[];
  loadingLessons: boolean;
  filters: AnalyticsLessonFilters;
  instructorName: string | null;
  dayItems: BarItem[];
  hourItems: BarItem[];
  onToggleHour: (key: string) => void;
  onToggleInstructor: (id: number) => void;
  onClearFilters: () => void;
}) {
  const { t } = useTranslation();
  const { totals } = overview;
  const hasFilter = filters.instructorId != null || filters.hour != null;

  return (
    <>
      <section
        aria-label={t("analytics.totals")}
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
          gap: "var(--space-3)",
        }}
      >
        <StatTile
          tone="brand"
          label={t("analytics.fillRate")}
          value={percent(totals.fill_rate)}
          icon={<Icon name="activity" size={18} />}
        />
        <StatTile
          tone="sage"
          label={t("analytics.utilization")}
          value={percent(totals.utilization)}
          icon={<Icon name="clock" size={18} />}
        />
        <StatTile
          tone="slate"
          label={t("analytics.booked")}
          value={formatNumber(totals.booked)}
          icon={<Icon name="users" size={18} />}
        />
        <StatTile
          tone="honey"
          label={t("analytics.students")}
          value={formatNumber(totals.students)}
          icon={<Icon name="user" size={18} />}
        />
        <StatTile
          tone="neutral"
          label={t("analytics.retention")}
          value={percent(totals.retention_rate)}
          icon={<Icon name="heart" size={18} />}
        />
      </section>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: "var(--space-4)",
        }}
      >
        <Card>
          <SectionTitle title={t("analytics.byDay")} hint={t("analytics.byDayHint")} />
          {dayItems.length === 0 ? (
            <EmptyRow label={t("analytics.empty")} />
          ) : (
            <BarChart
              items={dayItems}
              onSelect={undefined}
              testId="analytics-by-day"
              ariaLabel={t("analytics.byDay")}
            />
          )}
        </Card>

        <Card>
          <SectionTitle title={t("analytics.byHour")} hint={t("analytics.byHourHint")} />
          {hourItems.length === 0 ? (
            <EmptyRow label={t("analytics.empty")} />
          ) : (
            <BarChart
              items={hourItems}
              onSelect={onToggleHour}
              selectedKey={filters.hour != null ? String(filters.hour) : null}
              testId="analytics-by-hour"
              ariaLabel={t("analytics.byHour")}
            />
          )}
        </Card>
      </div>

      <Card>
        <SectionTitle title={t("analytics.byInstructor")} />
        {overview.by_instructor.length === 0 ? (
          <EmptyRow label={t("analytics.empty")} />
        ) : (
          <Table
            headers={[
              t("analytics.instructor"),
              t("analytics.lessons"),
              t("analytics.hours"),
              t("analytics.booked"),
              t("analytics.fillRate"),
            ]}
            testId="analytics-instructors"
          >
            {overview.by_instructor.map((bucket: AnalyticsInstructorBucket) => (
              <tr key={bucket.instructor_id}>
                <td style={CELL_STYLE}>
                  <button
                    type="button"
                    onClick={() => onToggleInstructor(bucket.instructor_id)}
                    aria-pressed={filters.instructorId === bucket.instructor_id}
                    data-testid={`instructor-${bucket.instructor_id}`}
                    style={linkButtonStyle}
                  >
                    {bucket.name?.trim() || t("common.instructor", { id: bucket.instructor_id })}
                  </button>
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {formatNumber(bucket.lessons)}
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {formatNumber(bucket.hours)}
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {t("analytics.bookedOf", { booked: bucket.booked, capacity: bucket.capacity })}
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {percent(bucket.fill_rate)}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card>
        <SectionTitle title={t("analytics.topStudents")} />
        {overview.top_students.length === 0 ? (
          <EmptyRow label={t("analytics.empty")} />
        ) : (
          <Table
            headers={[
              t("analytics.student"),
              t("analytics.bookings"),
              t("analytics.enrollments"),
              t("analytics.total"),
            ]}
            testId="analytics-students"
          >
            {overview.top_students.map((student: AnalyticsStudent) => (
              <tr key={student.id}>
                <td style={CELL_STYLE}>
                  <div style={{ fontWeight: 600 }}>{personName(student.name, student.email)}</div>
                  <div style={{ color: "var(--text-faint)", fontSize: 12 }}>{student.email}</div>
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {formatNumber(student.bookings)}
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {formatNumber(student.enrollments)}
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>
                  {formatNumber(student.total)}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <Card>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--space-3)",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <SectionTitle title={t("analytics.lessons")} hint={t("analytics.lessonsHint")} />
          <div style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
            {hasFilter ? (
              <>
                <span
                  data-testid="analytics-active-filter"
                  style={{
                    font: "var(--fw-medium) var(--fs-xs)/1 var(--font-body)",
                    color: "var(--brand-press)",
                    background: "var(--brand-soft)",
                    borderRadius: "var(--radius-full, 999px)",
                    padding: "6px 12px",
                  }}
                >
                  {filters.instructorId != null
                    ? t("analytics.filterInstructor", {
                        name:
                          instructorName?.trim() ||
                          t("common.instructor", { id: filters.instructorId }),
                      })
                    : t("analytics.filterHour", { hour: pad(filters.hour ?? 0) })}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  data-testid="analytics-clear-filter"
                >
                  {t("analytics.clearFilter")}
                </Button>
              </>
            ) : null}
          </div>
        </div>

        {loadingLessons ? (
          <EmptyRow label={t("common.loading")} />
        ) : lessons.length === 0 ? (
          <EmptyRow label={t("analytics.noLessons")} />
        ) : (
          <Table
            headers={[
              t("analytics.class"),
              t("analytics.instructor"),
              t("analytics.booked"),
              t("analytics.fillRate"),
            ]}
            testId="analytics-lessons"
          >
            {lessons.map((lesson) => (
              <tr key={lesson.id}>
                <td style={CELL_STYLE}>
                  <div style={{ fontWeight: 600 }}>{lesson.title}</div>
                  <div style={{ color: "var(--text-faint)", fontSize: 12 }}>
                    {formatShortDate(lesson.starts_at)} · {pad(lesson.hour)}:00
                  </div>
                </td>
                <td style={CELL_STYLE}>
                  {lesson.instructor_name?.trim() ||
                    t("common.instructor", { id: lesson.instructor_id })}
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {t("analytics.bookedOf", {
                    booked: lesson.booked,
                    capacity: lesson.capacity,
                  })}
                </td>
                <td style={{ ...CELL_STYLE, fontVariantNumeric: "tabular-nums" }}>
                  {percent(lesson.fill_rate)}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}

const linkButtonStyle: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  font: "inherit",
  color: "var(--brand-press)",
  cursor: "pointer",
  textAlign: "left",
};

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ marginBottom: "var(--space-2)" }}>
      <div
        style={{
          font: "var(--fw-bold) var(--fs-md)/1.2 var(--font-display)",
          color: "var(--text-strong)",
        }}
      >
        {title}
      </div>
      {hint ? (
        <div
          style={{
            font: "var(--fw-regular) var(--fs-xs)/1.4 var(--font-body)",
            color: "var(--text-faint)",
            marginTop: 2,
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

function Table({
  headers,
  testId,
  children,
}: {
  headers: string[];
  testId: string;
  children: ReactNode;
}) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table
        data-testid={testId}
        style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}
      >
        <thead>
          <tr>
            {headers.map((header) => (
              <th key={header} scope="col" style={{ ...ROW_LABEL_STYLE, padding: "6px 8px", textAlign: "left" }}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function EmptyRow({ label }: { label: string }) {
  return (
    <div
      style={{
        padding: "var(--space-5) 0",
        color: "var(--text-faint)",
        font: "var(--fw-regular) var(--fs-sm)/1.5 var(--font-body)",
      }}
    >
      {label}
    </div>
  );
}
